import type { MaterialNames } from "@/components/material_symbol_names";
import type { CareerPlanCompetencyScore } from "./careerPlanHistory";
import { indexCompletionPercent } from "./careerPlanHistory";
import type { CareerPlanRoleRubric } from "../queries";
import {
  EXPERIENCE_INDEX_CATEGORIES,
  getExperienceIndexCategoryById,
  getExperienceIndexCategoryId,
  normalizeExperienceCompetencyName,
  type ExperienceIndexCategoryId,
  type ExperienceIndexNamedCategoryId
} from "./experienceIndexCategories";
import { EXPERIENCE_INDEX_CATEGORY_ICONS } from "./experienceIndexIcons";

export const EXPERIENCE_INDEX_SCORE_MAX = 5;
export const RADAR_MINIMUM_COMPLETED_COMPETENCY_COUNT = 3;
export const COMPARISON_ROLE_PRIORITY_LIMIT = 6;
export const TOP_COMPATIBLE_ROLES_LIMIT = 3;

const HIGHLY_COMPATIBLE_FIT_PERCENT = 85;
const MODERATELY_COMPATIBLE_FIT_PERCENT = 65;

const CAREER_ROLE_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  "Management, Development, Ownership, Finance": "Regional Manager or Owner"
};

export type CareerRoleFit = CareerPlanRoleRubric & {
  completedCompetencyCount: number;
  fitPercent: number;
  matchedCompetencyCount: number;
};

export type RadarComparisonPoint = {
  name: string;
  member: number;
  desired?: number;
};

export type CompetencyStandingRow = {
  id: string;
  label: string;
  memberScore: number | null;
  desiredScore: number | null;
  isComplete: boolean;
  isPriority: boolean;
};

export type SkillGap = {
  competencyId: string;
  competencyName: string;
  desiredScore: number;
  gap: number;
  memberScore: number;
  priority: number;
};

export const PRIORITY_GAPS_LIMIT = 3;

function completedScoreMap(scores: CareerPlanCompetencyScore[]) {
  return new Map(
    scores
      .filter((score) => score.isComplete && typeof score.score === "number")
      .map((score) => [score.competencyId, score.score ?? 0])
  );
}

function competencyName(score: { competencyName: string }) {
  return score.competencyName.trim() || "Experience";
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function getRoleFitCompatibilityTierLabel(fitPercent: number) {
  if (fitPercent >= HIGHLY_COMPATIBLE_FIT_PERCENT) {
    return "Highly compatible";
  }

  if (fitPercent >= MODERATELY_COMPATIBLE_FIT_PERCENT) {
    return "Moderately compatible";
  }

  return "Compatible";
}

export function getCareerRoleDisplayName(role: Pick<CareerPlanRoleRubric, "classificationLabel" | "title">) {
  const classificationPrefix = new RegExp(
    `^${escapeRegExp(role.classificationLabel.trim())}(?:\\s*[-–—:]\\s*|\\s+)`,
    "i"
  );
  const roleName = role.title.trim().replace(classificationPrefix, "").replace(/[()]/g, "").trim();

  return CAREER_ROLE_DISPLAY_NAME_OVERRIDES[roleName] ?? roleName;
}

export function getTopCompatibleRoles(roleFits: CareerRoleFit[], limit = TOP_COMPATIBLE_ROLES_LIMIT) {
  return roleFits.slice(0, limit);
}

function categoryNameSet(categoryId?: ExperienceIndexCategoryId) {
  if (!categoryId) {
    return null;
  }

  return new Set(
    getExperienceIndexCategoryById(categoryId).competencyNames.map((name) => normalizeExperienceCompetencyName(name))
  );
}

function isCompetencyInCategory(competencyName: string, categoryId?: ExperienceIndexCategoryId) {
  const categoryNames = categoryNameSet(categoryId);
  if (!categoryNames) {
    return true;
  }

  return categoryNames.has(normalizeExperienceCompetencyName(competencyName));
}

function desiredScoreByCompetencyId(roleFit: CareerRoleFit | null) {
  return new Map(
    roleFit?.competencyScores.map((skillScore) => [skillScore.competencyId, skillScore.desiredScore]) ?? []
  );
}

function sortStandingRows(left: CompetencyStandingRow, right: CompetencyStandingRow) {
  if (left.isComplete !== right.isComplete) {
    return Number(right.isComplete) - Number(left.isComplete);
  }

  if (left.isComplete && right.isComplete && left.memberScore !== right.memberScore) {
    return (right.memberScore ?? 0) - (left.memberScore ?? 0);
  }

  return left.label.localeCompare(right.label);
}

export function getCareerRoleFits(
  rubrics: CareerPlanRoleRubric[],
  scores: CareerPlanCompetencyScore[]
): CareerRoleFit[] {
  const scoreByCompetencyId = completedScoreMap(scores);

  return rubrics
    .map((rubric) => {
      const comparableScores = rubric.competencyScores.filter((skillScore) =>
        scoreByCompetencyId.has(skillScore.competencyId)
      );
      const matchedCompetencyCount = comparableScores.filter((skillScore) => {
        const memberScore = scoreByCompetencyId.get(skillScore.competencyId) ?? 0;
        return memberScore >= skillScore.desiredScore;
      }).length;

      return {
        ...rubric,
        completedCompetencyCount: comparableScores.length,
        fitPercent:
          comparableScores.length > 0 ? Math.round((matchedCompetencyCount / comparableScores.length) * 100) : 0,
        matchedCompetencyCount
      };
    })
    .sort((a, b) => {
      const fitSort = b.fitPercent - a.fitPercent;
      if (fitSort !== 0) return fitSort;

      const matchedSort = b.matchedCompetencyCount - a.matchedCompetencyCount;
      if (matchedSort !== 0) return matchedSort;

      return a.title.localeCompare(b.title);
    });
}

export function getTopDesiredRoleCompetencyIds(roleFit: CareerRoleFit | null, limit = COMPARISON_ROLE_PRIORITY_LIMIT) {
  if (!roleFit) {
    return new Set<string>();
  }

  return new Set(
    [...roleFit.competencyScores]
      .sort(
        (left, right) =>
          right.desiredScore - left.desiredScore || competencyName(left).localeCompare(competencyName(right))
      )
      .slice(0, limit)
      .map((skillScore) => skillScore.competencyId)
  );
}

export function getPriorityWeightedGaps(
  roleFit: CareerRoleFit | null,
  scores: CareerPlanCompetencyScore[]
): SkillGap[] {
  if (!roleFit) {
    return [];
  }

  const scoreByCompetencyId = completedScoreMap(scores);

  return roleFit.competencyScores
    .map((skillScore) => {
      const memberScore = scoreByCompetencyId.get(skillScore.competencyId);
      if (typeof memberScore !== "number") {
        return null;
      }

      const gap = Math.max(skillScore.desiredScore - memberScore, 0);
      return {
        competencyId: skillScore.competencyId,
        competencyName: competencyName(skillScore),
        desiredScore: skillScore.desiredScore,
        gap,
        memberScore,
        priority: gap * skillScore.desiredScore
      };
    })
    .filter((gap): gap is SkillGap => gap !== null && gap.gap > 0)
    .sort((a, b) => b.priority - a.priority || b.gap - a.gap || a.competencyName.localeCompare(b.competencyName));
}

export function getTopGaps(
  roleFit: CareerRoleFit | null,
  scores: CareerPlanCompetencyScore[],
  limit = PRIORITY_GAPS_LIMIT
) {
  return getPriorityWeightedGaps(roleFit, scores).slice(0, limit);
}

export function getRadarComparisonData(
  roleFit: CareerRoleFit | null,
  scores: CareerPlanCompetencyScore[],
  categoryId?: ExperienceIndexCategoryId
): RadarComparisonPoint[] {
  const scoreByCompetencyId = completedScoreMap(scores);
  const desiredByCompetencyId = desiredScoreByCompetencyId(roleFit);

  return scores
    .filter((score) => {
      if (!isCompetencyInCategory(score.competencyName, categoryId)) {
        return false;
      }

      if (!scoreByCompetencyId.has(score.competencyId)) {
        return false;
      }

      return !roleFit || desiredByCompetencyId.has(score.competencyId);
    })
    .map((score) => {
      const point: RadarComparisonPoint = {
        name: competencyName(score),
        member: scoreByCompetencyId.get(score.competencyId) ?? 0
      };
      const desiredScore = desiredByCompetencyId.get(score.competencyId);
      if (desiredScore != null) {
        point.desired = desiredScore;
      }

      return point;
    });
}

export function getCompetencyStandingRows(
  roleFit: CareerRoleFit | null,
  scores: CareerPlanCompetencyScore[],
  categoryId: ExperienceIndexCategoryId
): CompetencyStandingRow[] {
  const scoreByCompetencyId = completedScoreMap(scores);
  const desiredByCompetencyId = desiredScoreByCompetencyId(roleFit);
  const priorityIds = getTopDesiredRoleCompetencyIds(roleFit);

  return scores
    .filter((score) => isCompetencyInCategory(score.competencyName, categoryId))
    .map((score) => {
      const memberScore = scoreByCompetencyId.get(score.competencyId) ?? null;

      return {
        id: score.competencyId,
        label: competencyName(score),
        memberScore,
        desiredScore: desiredByCompetencyId.get(score.competencyId) ?? null,
        isComplete: memberScore != null,
        isPriority: priorityIds.has(score.competencyId)
      };
    })
    .sort(sortStandingRows);
}

export function canShowExperienceIndexRadar(points: RadarComparisonPoint[], categoryId: ExperienceIndexCategoryId) {
  if (categoryId === "all") {
    return false;
  }

  return points.length >= RADAR_MINIMUM_COMPLETED_COMPETENCY_COUNT;
}

export type ExperienceIndexNamedCompetency = {
  id: string;
  label: string;
};

export type ExperienceIndexHubCompetency = ExperienceIndexNamedCompetency & {
  score: number;
};

export function formatExperienceIndexScoreValue(score: number): string {
  const rounded = Math.round(score * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatExperienceIndexHubScore(score: number): string {
  return `Score: ${formatExperienceIndexScoreValue(score)}`;
}

export function experienceIndexScorePercent(score: number): number {
  return Math.max(0, Math.min(100, (score / EXPERIENCE_INDEX_SCORE_MAX) * 100));
}

function completedNamedCompetencies(scores: CareerPlanCompetencyScore[]): ExperienceIndexHubCompetency[] {
  return scores.flatMap((score) => {
    if (!score.isComplete || typeof score.score !== "number") return [];

    return [
      {
        id: score.competencyId,
        label: competencyName(score),
        score: score.score
      }
    ];
  });
}

function sortNamedCompetenciesByScore(competencies: ExperienceIndexHubCompetency[], direction: "highest" | "lowest") {
  return [...competencies].sort((a, b) => {
    const scoreSort = direction === "highest" ? b.score - a.score : a.score - b.score;
    if (scoreSort !== 0) return scoreSort;
    return a.label.localeCompare(b.label);
  });
}

export type ExperienceIndexCategoryCompletion = {
  id: ExperienceIndexNamedCategoryId;
  label: string;
  iconName: MaterialNames;
  answered: number;
  total: number;
  percent: number | null;
};

export function getExperienceIndexCategoryCompletions(
  scores: Array<Pick<CareerPlanCompetencyScore, "competencyName" | "answeredQuestionCount" | "totalQuestionCount">>
): ExperienceIndexCategoryCompletion[] {
  return EXPERIENCE_INDEX_CATEGORIES.map((category) => {
    const matchingScores = scores.filter((score) => getExperienceIndexCategoryId(score.competencyName) === category.id);
    const answered = matchingScores.reduce((sum, score) => sum + score.answeredQuestionCount, 0);
    const total = matchingScores.reduce((sum, score) => sum + score.totalQuestionCount, 0);

    return {
      id: category.id,
      label: category.label,
      iconName: EXPERIENCE_INDEX_CATEGORY_ICONS[category.id],
      answered,
      total,
      percent: indexCompletionPercent(answered, total)
    };
  });
}

export function getHubTopStrengths(scores: CareerPlanCompetencyScore[], limit = 3): ExperienceIndexHubCompetency[] {
  return sortNamedCompetenciesByScore(completedNamedCompetencies(scores), "highest").slice(0, limit);
}

export function getHubAreasToDiscuss(scores: CareerPlanCompetencyScore[], limit = 3): ExperienceIndexHubCompetency[] {
  const strengthIds = new Set(getHubTopStrengths(scores, limit).map((item) => item.id));
  const remaining = completedNamedCompetencies(scores).filter((item) => !strengthIds.has(item.id));
  return sortNamedCompetenciesByScore(remaining, "lowest").slice(0, limit);
}
